import React, { useState, useEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiEye } from "react-icons/fi";
import { getZohoTags } from "../../Services/zohoTagService";
import { CommonTable, Loader } from "../../Components/Common";
import TagOptionsModal from "./Components/TagOptionsModal";
import {
  MESSAGES,
  ZOHO_TAG_LIST_COLUMNS,
  ZOHO_TAG_LIST_LABELS,
  ZOHO_TAG_BOOLEAN_COLUMNS,
} from "./constants";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

export const ZohoTagList = () => {
  const [tagList, setTagList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [selectedTag, setSelectedTag] = useState(null);

  const getPaginatedTags = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response = await getZohoTags(finalParams);
      if (response.status === 200) {
        const newTags = response.data.results || [];
        setTagList((prev) => (isInitial ? newTags : [...prev, ...newTags]));
        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      } else {
        setPaginationLoading(false);
      }
    }
  };

  const loadMoreTags = () => {
    if (pagination.offset >= pagination.count) return;
    getPaginatedTags({ offset: pagination.offset, limit: pagination.limit });
  };

  const renderFields = (key, row) => {
    if (key === "options_count") {
      return row.options_count ?? row.options?.length ?? 0;
    }
    if (key === "actions") {
      return (
        <div
          title="View Options"
          className="text-primary cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTag(row);
          }}
        >
          <FiEye size={20} />
        </div>
      );
    }
    if (ZOHO_TAG_BOOLEAN_COLUMNS.includes(key)) {
      return row[key] ? "Yes" : "No";
    }
    return row[key] === "" || row[key] === null || row[key] === undefined
      ? "-"
      : row[key];
  };

  useEffect(() => {
    getPaginatedTags({ offset: 0, limit: pagination.limit }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="px-4 my-4">
      <div className="flex items-center justify-between">
        <div className="text-xl md:text-2xl text-primary font-semibold mb-3">
          Reporting Tags
        </div>
      </div>

      <div
        className="h-[68vh] overflow-x-auto overflow-y-auto md:border md:rounded-md"
        id="zohoTagScrollableDiv"
      >
        {initialLoading ? (
          <div className="flex justify-center items-center h-full py-10">
            <Loader size={4} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={tagList.length}
            next={loadMoreTags}
            hasMore={pagination.offset < pagination.count}
            scrollableTarget="zohoTagScrollableDiv"
          >
            <CommonTable
              tableColumns={ZOHO_TAG_LIST_COLUMNS}
              tableRows={tagList}
              columnLabels={ZOHO_TAG_LIST_LABELS}
              renderField={renderFields}
              customError={MESSAGES.NO_TAGS_AVAILABLE}
            />
            {paginationLoading && (
              <div className="flex justify-center py-4">
                <Loader size={3} />
              </div>
            )}
          </InfiniteScroll>
        )}
      </div>

      {selectedTag && (
        <TagOptionsModal
          tag={selectedTag}
          onClose={() => setSelectedTag(null)}
        />
      )}
    </div>
  );
};

export default ZohoTagList;
